import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Tabs,
  Tab,
  Alert,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaUserGraduate,
  FaBook,
  FaCalendar,
  FaPhone,
  FaEnvelope,
  FaPrint,
  FaPlus,
  FaTag,
  FaHistory,
  FaDownload,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import StudentService from "../../../services/studentService";
import AddPaymentModal from "./AddPaymentModal";
import ReceiptView from "./ReceiptView";
import PaymentService from "../../../services/paymentService";
import DiscountModal from "../../../components/admin/DiscountModal";

const StudentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [courses, setCourses] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Modals
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [activeTab, setActiveTab] = useState("courses");
  const [showDiscountModal, setShowDiscountModal] = useState(false);

  useEffect(() => {
    loadStudentDetails();
  }, [id]);

  // Helper function to calculate discounted fee
  const getDiscountedFee = (course) => {
    const discount = course.discount_amount || 0;
    return course.total_fees - discount;
  };

  // Helper function to calculate due amount with discount
  const getDueAmountWithDiscount = (course) => {
    const discountedFee = getDiscountedFee(course);
    const paid = course.paid_amount || 0;
    return discountedFee - paid;
  };

  // Helper function to calculate progress with discount
  const getProgressWithDiscount = (course) => {
    const discountedFee = getDiscountedFee(course);
    const paid = course.paid_amount || 0;
    if (discountedFee <= 0) return 100;
    return (paid / discountedFee) * 100;
  };

  const loadStudentDetails = async () => {
    try {
      setLoading(true);
      const response = await StudentService.getStudentDetails(id);
      setStudent(response.data.student);

      // FIXED: Calculate due_amount correctly with discount
      const coursesWithDiscount = (response.data.courses || []).map(
        (course) => ({
          ...course,
          discounted_fee: course.total_fees - (course.discount_amount || 0),
          due_amount:
            course.total_fees -
            (course.discount_amount || 0) -
            (course.paid_amount || 0),
        }),
      );

      setCourses(coursesWithDiscount);
      setTransactions(response.data.transactions || []);

      // FIXED: Recalculate summary with discount
      const totalFees = coursesWithDiscount.reduce(
        (sum, c) => sum + c.total_fees,
        0,
      );
      const totalDiscount = coursesWithDiscount.reduce(
        (sum, c) => sum + (c.discount_amount || 0),
        0,
      );
      const totalPaid = coursesWithDiscount.reduce(
        (sum, c) => sum + (c.paid_amount || 0),
        0,
      );
      const totalDue = coursesWithDiscount.reduce(
        (sum, c) => sum + (c.due_amount || 0),
        0,
      );

      setSummary({
        total_courses: coursesWithDiscount.length,
        total_fees: totalFees,
        total_discount: totalDiscount,
        total_paid: totalPaid,
        total_due: totalDue,
      });
    } catch (error) {
      toast.error("Failed to load student details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowAddPayment(false);
    loadStudentDetails(); // Reload all data
    toast.success("Payment added successfully");
  };

  const handleDiscountSuccess = () => {
    setShowDiscountModal(false);
    loadStudentDetails(); // Reload all data
    toast.success("Discount applied successfully");
  };

  const handleViewReceipt = (transaction) => {
    setSelectedTransaction(transaction);
    setShowReceipt(true);
  };

  // Export Transactions Handler
  const handleExportTransactions = async () => {
    try {
      setExporting(true);
      await PaymentService.exportTransactions({
        student_id: id,
        start_date: null,
        end_date: null,
      });
      toast.success(`Transactions for ${student?.name} exported successfully!`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error(
        error.response?.data?.message || "Failed to export transactions",
      );
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="container-fluid p-4 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div>
        <Header />
        <div className="container-fluid p-4 text-center">
          <h3>Student not found</h3>
          <Button variant="primary" onClick={() => navigate("/admin/students")}>
            Back to Students
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        {/* Back Button */}
        <Button
          variant="link"
          className="mb-3 text-decoration-none"
          onClick={() => navigate("/admin/students")}
        >
          <FaArrowLeft className="me-2" /> Back to Fees
        </Button>

        {/* Student Header Card */}
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <Row>
              <Col md={8}>
                <div className="d-flex align-items-center">
                  <div className="bg-primary bg-opacity-10 p-4 rounded-circle me-4">
                    <FaUserGraduate size={40} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="mb-2">{student.name}</h2>
                    <div className="d-flex gap-4 flex-wrap">
                      <div>
                        <FaEnvelope className="me-2 text-muted" />
                        <span>{student.email}</span>
                      </div>
                      <div>
                        <FaPhone className="me-2 text-muted" />
                        <span>{student.phone}</span>
                      </div>
                      <div>
                        <FaCalendar className="me-2 text-muted" />
                        <span>Joined: {formatDate(student.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-end">
                <div className="d-flex gap-2 justify-content-end">
                  <Button
                    variant="success"
                    onClick={() => setShowAddPayment(true)}
                  >
                    <FaPlus className="me-2" /> Add Payment
                  </Button>

                  <Button
                    variant="warning"
                    onClick={() => setShowDiscountModal(true)}
                  >
                    <FaTag className="me-2" /> Apply Discount
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Summary Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-primary text-white">
              <Card.Body>
                <h6>Total Courses</h6>
                <h3>{summary.total_courses || 0}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-success text-white">
              <Card.Body>
                <h6>Total Fees</h6>
                <h3>{formatCurrency(summary.total_fees)}</h3>
                {summary.total_discount > 0 && (
                  <small>
                    Discount: {formatCurrency(summary.total_discount)}
                  </small>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-info text-white">
              <Card.Body>
                <h6>Total Paid</h6>
                <h3>{formatCurrency(summary.total_paid)}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-warning text-white">
              <Card.Body>
                <h6>Total Due</h6>
                <h3>{formatCurrency(summary.total_due)}</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabs for Courses and Transactions */}
        <Card className="shadow-sm">
          <Card.Header>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-0"
            >
              <Tab
                eventKey="courses"
                title={
                  <span>
                    <FaBook className="me-2" /> Enrolled Courses
                  </span>
                }
              >
                <div className="p-3">
                  {courses.length === 0 ? (
                    <Alert variant="info">No courses enrolled yet.</Alert>
                  ) : (
                    <Table striped hover responsive>
                      <thead>
                        <tr>
                          <th>Course</th>
                          <th>Duration</th>
                          <th>Original Fee</th>
                          <th>Discount</th>
                          <th>Discounted Fee</th>
                          <th>Paid</th>
                          <th>Due</th>
                          <th>Status</th>
                          <th>Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {courses.map((course) => {
                          const discountedFee =
                            course.total_fees - (course.discount_amount || 0);
                          const dueAmount =
                            discountedFee - (course.paid_amount || 0);
                          const progress =
                            discountedFee > 0
                              ? (course.paid_amount / discountedFee) * 100
                              : 100;

                          return (
                            <tr key={course.id}>
                              <td>
                                <strong>{course.course_name}</strong>
                              </td>
                              <td>{course.duration}</td>
                              <td>{formatCurrency(course.total_fees)}</td>
                              <td className="text-success">
                                {course.discount_amount > 0
                                  ? formatCurrency(course.discount_amount)
                                  : "-"}
                              </td>
                              <td className="fw-bold">
                                {formatCurrency(discountedFee)}
                              </td>
                              <td className="text-info">
                                {formatCurrency(course.paid_amount)}
                              </td>
                              <td
                                className={
                                  dueAmount > 0
                                    ? "text-danger fw-bold"
                                    : "text-success fw-bold"
                                }
                              >
                                {formatCurrency(dueAmount)}
                              </td>
                              <td>
                                {dueAmount === 0 ? (
                                  <Badge bg="success">Paid</Badge>
                                ) : (
                                  <Badge bg="warning">Due</Badge>
                                )}
                              </td>
                              <td style={{ width: "150px" }}>
                                <div className="d-flex align-items-center">
                                  <div className="flex-grow-1 me-2">
                                    <div
                                      className="progress"
                                      style={{ height: "8px" }}
                                    >
                                      <div
                                        className={`progress-bar bg-${progress === 100 ? "success" : "primary"}`}
                                        style={{ width: `${progress}%` }}
                                      />
                                    </div>
                                  </div>
                                  <small>{Math.round(progress)}%</small>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Tab>

              <Tab
                eventKey="transactions"
                title={
                  <span>
                    <FaHistory className="me-2" /> Transaction History
                  </span>
                }
              >
                <div className="p-3">
                  {/* Export Button for Transactions */}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">All Transactions</h6>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleExportTransactions}
                      disabled={exporting || transactions.length === 0}
                    >
                      {exporting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <FaDownload className="me-2" />
                          Export Transactions
                        </>
                      )}
                    </Button>
                  </div>

                  {transactions.length === 0 ? (
                    <Alert variant="info">No transactions found.</Alert>
                  ) : (
                    <Table striped hover responsive>
                      <thead>
                        <tr>
                          <th>Receipt No</th>
                          <th>Date</th>
                          <th>Course</th>
                          <th>Amount</th>
                          <th>Method</th>
                          <th>Paid By</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((t) => (
                          <tr key={t.id}>
                            <td>
                              <code>{t.receipt_number}</code>
                            </td>
                            <td>{formatDate(t.payment_date)}</td>
                            <td>{t.course_name}</td>
                            <td
                              className={`fw-bold ${t.amount < 0 ? "text-danger" : "text-success"}`}
                            >
                              {t.amount < 0
                                ? `- ${formatCurrency(Math.abs(t.amount))}`
                                : formatCurrency(t.amount)}
                            </td>
                            <td>
                              <Badge
                                bg={
                                  t.payment_method === "Discount"
                                    ? "danger"
                                    : "secondary"
                                }
                              >
                                {t.payment_method === "Discount"
                                  ? "Discount"
                                  : t.payment_method}
                              </Badge>
                            </td>
                            <td>
                              <Badge
                                bg={t.paid_by === "admin" ? "info" : "primary"}
                              >
                                {t.paid_by === "admin" ? "Admin" : "Student"}
                              </Badge>
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewReceipt(t)}
                              >
                                <FaPrint className="me-1" /> Receipt
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Tab>
            </Tabs>
          </Card.Header>
        </Card>

        {/* Add Payment Modal */}
        <AddPaymentModal
          show={showAddPayment}
          onHide={() => setShowAddPayment(false)}
          studentId={id}
          studentName={student.name}
          courses={courses}
          onSuccess={handlePaymentSuccess}
        />

        {/* Receipt View Modal */}
        <ReceiptView
          show={showReceipt}
          onHide={() => {
            setShowReceipt(false);
            setSelectedTransaction(null);
          }}
          transaction={selectedTransaction}
        />

        {/* Discount Modal */}
        <DiscountModal
          show={showDiscountModal}
          onHide={() => setShowDiscountModal(false)}
          studentId={id}
          studentName={student.name}
          courses={courses}
          onSuccess={handleDiscountSuccess}
        />
      </div>
    </div>
  );
};

export default StudentDetails;
